import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { templatesListService } from '../services/templates.service';
import type { TemplateFilters } from '../types/templates.types';

export function useTemplates() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<TemplateFilters>({ query: '', type: 'all' });

  const query = useQuery({
    queryKey: ['settings', 'templates', 'list', companyId],
    queryFn: () => templatesListService.getTemplates(companyId),
    enabled: !!companyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesListService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'templates', 'list'] });
      toast.success('Đã xóa mẫu tài liệu');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Không thể xóa mẫu tài liệu');
    }
  });

  const templates = useMemo(() => {
    const search = filters.query.toLowerCase().trim();
    return (query.data || []).filter(t => {
      const matchesSearch = !search || t.name.toLowerCase().includes(search) || t.code.toLowerCase().includes(search);
      const matchesType = filters.type === 'all' || t.type === filters.type;
      return matchesSearch && matchesType;
    });
  }, [query.data, filters]);

  return {
    templates,
    filters,
    isLoading: query.isLoading,
    setFilters,
    deleteTemplate: (id: string) => deleteMutation.mutate(id),
  };
}
